import { timestamp } from "./format";
import type {
  PaymentProvider,
  PaymentProviderCredential,
  PaymentProviderEnvironment,
} from "./types";

/**
 * The pure half of the Payment providers page: the form's draft, what it sends,
 * and how a credential is described.
 *
 * Mirrors `server/src/api/routes/paymentProviders.ts`'s bounds so a field the
 * server would refuse is marked before the request. Secrets only ever travel one
 * way — from this form to the server — and a blank secret field on an edit means
 * "keep the one stored", because the stored one is never shown.
 */

export const LABEL_MAX = 80;
export const MERCHANT_ID_MAX = 64;
export const API_KEY_MAX = 512;
export const PEM_MAX = 4096;

export interface ProviderDraft {
  readonly label: string;
  readonly environment: PaymentProviderEnvironment;
  readonly merchantId: string;
  /** Blank on an edit keeps the stored key. */
  readonly apiKey: string;
  /** Blank on an edit keeps the stored key. */
  readonly refundPublicKey: string;
  readonly isDefault: boolean;
}

export type ProviderDraftErrors = Partial<Record<keyof ProviderDraft, string>>;

/** A blank form, or one holding a provider's non-secret fields. */
export function emptyDraft(from?: PaymentProvider): ProviderDraft {
  return {
    label: from?.label ?? "",
    environment: from?.environment ?? "sandbox",
    merchantId: from?.merchantId ?? "",
    apiKey: "",
    refundPublicKey: "",
    isDefault: from?.isDefault ?? false,
  };
}

/** "Set · 15 Oct 2026, 10:00 by Visal", or "Not set". Never a fragment of the key. */
export function credentialStatus(credential: PaymentProviderCredential, timeZone: string): string {
  if (!credential.set) return "Not set";
  const when =
    credential.updatedAt === null ? "" : ` · ${timestamp(credential.updatedAt, timeZone)}`;
  const who = credential.updatedBy === null ? "" : ` by ${credential.updatedBy}`;
  return `Set${when}${who}`;
}

const PEM_SHAPE = /^-----BEGIN PUBLIC KEY-----[\s\S]+-----END PUBLIC KEY-----$/;

/** Accepts the escaped `\n` the server does, for a key copied out of an env file. */
function normalisePem(pem: string): string {
  return pem.replace(/\\n/g, "\n").trim();
}

export function providerDraftErrors(
  draft: ProviderDraft,
  mode: "create" | "edit",
): ProviderDraftErrors {
  const errors: { -readonly [K in keyof ProviderDraft]?: string } = {};
  const label = draft.label.trim();
  const merchantId = draft.merchantId.trim();
  const apiKey = draft.apiKey.trim();
  const pem = normalisePem(draft.refundPublicKey);

  if (label === "") errors.label = "A name operators will recognise";
  else if (label.length > LABEL_MAX) errors.label = `At most ${LABEL_MAX} characters`;

  if (merchantId === "") errors.merchantId = "The merchant id ABA issued";
  else if (merchantId.length > MERCHANT_ID_MAX) {
    errors.merchantId = `At most ${MERCHANT_ID_MAX} characters`;
  }

  if (mode === "create" && apiKey === "") errors.apiKey = "The API key ABA issued";
  else if (apiKey.length > API_KEY_MAX) errors.apiKey = `At most ${API_KEY_MAX} characters`;

  if (pem !== "" && (pem.length > PEM_MAX || !PEM_SHAPE.test(pem))) {
    errors.refundPublicKey = "Paste the whole PEM, from BEGIN PUBLIC KEY to END PUBLIC KEY";
  }

  return errors;
}

export interface CreateProviderBody {
  readonly type: "payway";
  readonly label: string;
  readonly environment: PaymentProviderEnvironment;
  readonly merchantId: string;
  readonly apiKey: string;
  readonly refundPublicKey?: string;
  readonly isDefault: boolean;
}

/** Only call on a draft `providerDraftErrors` passed. */
export function createBody(draft: ProviderDraft): CreateProviderBody {
  const pem = normalisePem(draft.refundPublicKey);
  return {
    type: "payway",
    label: draft.label.trim(),
    environment: draft.environment,
    merchantId: draft.merchantId.trim(),
    apiKey: draft.apiKey.trim(),
    ...(pem === "" ? {} : { refundPublicKey: pem }),
    isDefault: draft.isDefault,
  };
}

export interface UpdateProviderBody {
  readonly label?: string;
  readonly environment?: PaymentProviderEnvironment;
  readonly merchantId?: string;
  readonly apiKey?: string;
  readonly refundPublicKey?: string | null;
}

/**
 * The PATCH for an edit: changed fields only, a typed secret as a rotation, and
 * `refundPublicKey: null` when the operator removes the RSA key. `undefined` when
 * there is nothing to send. Only call on a draft `providerDraftErrors` passed.
 */
export function updateBody(
  current: PaymentProvider,
  draft: ProviderDraft,
  removeRefundKey: boolean,
): UpdateProviderBody | undefined {
  const label = draft.label.trim();
  const merchantId = draft.merchantId.trim();
  const apiKey = draft.apiKey.trim();
  const pem = normalisePem(draft.refundPublicKey);

  const body: UpdateProviderBody = {
    ...(label === current.label ? {} : { label }),
    ...(draft.environment === current.environment ? {} : { environment: draft.environment }),
    ...(merchantId === current.merchantId ? {} : { merchantId }),
    ...(apiKey === "" ? {} : { apiKey }),
    ...(removeRefundKey ? { refundPublicKey: null } : pem === "" ? {} : { refundPublicKey: pem }),
  };
  return Object.keys(body).length === 0 ? undefined : body;
}

/** Why a provider cannot be deleted, mirroring the server's 409; `undefined` if it can. */
export function deleteBlocker(provider: PaymentProvider): string | undefined {
  if (provider.isDefault) return "It is the default. Make another provider the default first.";
  if (provider.depots.length > 0) return "Depots use it. Move them to another provider first.";
  if (provider.orderCount > 0) {
    return "It has taken orders, which refund through it, so it is kept.";
  }
  return undefined;
}
